/**
 * Snakes and Ladders AI
 *
 * Since the game is purely luck-based (no strategic decisions),
 * AI difficulty only affects the delay before auto-rolling the dice.
 *
 * Easy: 2000ms, Medium: 1000ms, Hard: 500ms
 */

import { SnakesLaddersDifficulty } from './types';

export class SnakesLaddersAI {
  private difficulty: SnakesLaddersDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: SnakesLaddersDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  /** Get the delay in ms before the AI rolls the dice */
  getDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return 2000;
      case 'medium':
        return 1000;
      case 'hard':
        return 500;
      default:
        return 1000;
    }
  }

  /** Set the AI difficulty */
  setDifficulty(difficulty: SnakesLaddersDifficulty): void {
    this.difficulty = difficulty;
  }

  /** Get the current difficulty */
  getDifficulty(): SnakesLaddersDifficulty {
    return this.difficulty;
  }

  /**
   * Schedule an auto-roll after the configured delay.
   * Calls the onRoll callback when it's time to roll.
   * Returns a cancel function.
   */
  scheduleRoll(onRoll: () => void): () => void {
    this.cancelPendingRoll();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      onRoll();
    }, delay);

    return () => this.cancelPendingRoll();
  }

  /** Cancel any pending auto-roll */
  cancelPendingRoll(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  /** Clean up resources */
  destroy(): void {
    this.cancelPendingRoll();
  }
}
